import Card from '../../../components/card/Card';
import P from '../../../components/texts/p/P';
import DropDown from '../../../components/dropdown/DropDown';
import Input from '../../../components/input/Input';
import Button from '../../../components/button/Button';
import { useClientTheme } from '../../../context/ThemeContext';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { CURRENCY_CODES, MONTH_NAMES } from '../../../utils/Constants';
import {
  postBalanceSheetData,
  salesPurchasesCloudData,
} from '../../../types/interfaces';
import { formatCurrency } from '../../../utils/Masks';
import Span from '../../../components/texts/span/Span';
import H from '../../../components/texts/h/H';
import Icon from '../../../components/icon/Icon';
import { NumericFormat } from 'react-number-format';
import { toNumber } from '../../../utils/Utils';

interface PostBalanceSheetProps {
  onChangeSheet: Function;
  subtitle: string;
  totalText: string;
  validateForms: boolean | null;
  cloudData: salesPurchasesCloudData;
  localData: any;
  disableFields: boolean;
  showAllErrors?: boolean;
  balanceLastMonth: any;
  inputMarginTop?: string;
}

const PostBalanceSheet: React.FC<PostBalanceSheetProps> = ({
  onChangeSheet,
  subtitle,
  totalText,
  validateForms,
  cloudData,
  localData,
  disableFields,
  showAllErrors = false,
  balanceLastMonth,
  inputMarginTop = '0px'
}) => {
  const { clientTheme } = useClientTheme();
  const [disableAddSheetMonth, setDisableAddSheetMonth] =
    useState<boolean>(true);
  const [months, setMonths] = useState<any[]>([]);
  const currentMonthIndex =
    new Date().getFullYear() * 12 + new Date().getMonth();
  const [totalAmounts, setTotalAmounts] = useState<number>(0);
  const [shouldValidateForm, setShouldValidateForm] = useState<boolean>(false);
  const currentDate = new Date();
    const startMonth = (balanceLastMonth.to_month ?? 0) + 1; 
    const startDate = new Date(balanceLastMonth.to_year ?? currentDate.getFullYear(), startMonth - 1); 

    const diffInMonths =
      (currentDate.getFullYear() - startDate.getFullYear()) * 12 +
      (currentDate.getMonth() - startDate.getMonth());

  useEffect(() => {
    let monthsArray: any[] = [];

    for (let i = 0; i <= diffInMonths; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1
      );
      const formattedMonth = `${MONTH_NAMES[date.getMonth()]} - ${date.getFullYear()}`;

      monthsArray.push({
        id: diffInMonths - i,
        label: formattedMonth,
      });
    }
    monthsArray = monthsArray.reverse();
    setMonths(monthsArray);

    if (localData && localData.length > 0) {
      const transformedData = localData.map((item) => ({
        month: item.month,
        monthId: monthsArray.find(
          (month) =>
            month.label === MONTH_NAMES[item.month - 1] + ' - ' + item.year
        )?.id,
        amount: {
          currency_code: CURRENCY_CODES.ars,
          value:
            typeof item.amount === 'number'
              ? item.amount
              : toNumber(item.amount),
        },
        year: item.year,
      }));
      sheetFormik.setValues(transformedData);
    }
  }, []);

  useEffect(() => {
    if (validateForms !== null) {
      setShouldValidateForm(true);
      sheetFormik.validateForm();
    }
  }, [validateForms]);

  useEffect(() => {
    if (cloudData) {
      const transformedValues = Object.entries(cloudData).map(
        ([key, value], index) => {
          return {
            id: index + 1,
            monthId: index + 1,
            month: key.slice(4, 6),
            year: key.slice(0, 4),
            amount: {
              currency_code: CURRENCY_CODES.ars,
              value: typeof value === 'number' ? value : toNumber(value),
            },
          };
        }
      );
      handleMonth(
        MONTH_NAMES[parseInt(transformedValues[0].month) - 1] +
          ' - ' +
          transformedValues[0].year,
        transformedValues,
        transformedValues.length
      );
    }
  }, [cloudData]);

  const validateSheetData = (values: postBalanceSheetData[]) => {
    const errors: any[] = [];
    values.forEach((product, index) => {
      const sheetErrors: any = {};
      if (
        product.amount?.value === '' ||
        product.amount?.value === null ||
        product.amount?.value === undefined
      ) {
        sheetErrors.amount = "Debés completar el campo 'Monto'";
      }
      if (product.monthId === null || product.monthId === undefined) {
        sheetErrors.month = 'Debés seleccionar un mes';
      }
      errors[index] = sheetErrors;
    });
    return errors;
  };

  const sheetFormik = useFormik({
    initialValues: [
      {
        id: 1,
        monthId: null,
        month: null,
        year: null,
        amount: {
          currency_code: CURRENCY_CODES.ars,
          value: 0,
        },
      },
    ],
    validate: validateSheetData,
    onSubmit: (values) => {},
  });

  useEffect(() => {
    if (showAllErrors) {
      const touchedObj: any = {};
      sheetFormik.values.forEach((_, idx) => {
        touchedObj[idx] = {
          monthId: true,
          amount: { value: true },
        };
      });
      sheetFormik.setTouched(touchedObj, false);
    }
  }, [showAllErrors, sheetFormik.values.length]);

  useEffect(() => {
    updateTotals();
    sheetFormik.values[sheetFormik.values.length - 1].monthId === diffInMonths ||
    sheetFormik.values[0].monthId === null
      ? setDisableAddSheetMonth(true)
      : setDisableAddSheetMonth(false);

    const isValid =
      sheetFormik.values.length > 0 &&
      sheetFormik.values.every((row, idx) => {
        const err = sheetFormik.errors[idx] || {};
        return !err.month && !err.amount;
      });

    onChangeSheet({ values: sheetFormik.values, isValid });
  }, [sheetFormik.values, sheetFormik.errors]);

  const handleMonth = (
    event: string,
    data: any = null,
    monthsToDisplay: number = 6
  ) => {
    setShouldValidateForm(false);
    const selectedIndex = months.find((item) => item.label === event).id;
    const allowedMonths = Math.min(
      monthsToDisplay,
      currentMonthIndex - selectedIndex + 1
    );
    const inputsToRender = months
      .slice(selectedIndex, selectedIndex + allowedMonths)
      .map((month, index) => ({
        id: month.id,
        monthId: month.id,
        month:
          MONTH_NAMES.findIndex(
            (newMonth) => newMonth === month.label.split('-').shift().trim()
          ) + 1,
        year: parseInt(month.label.split('-').pop().trim()),
        amount: {
          currency_code: CURRENCY_CODES.ars,
          value: toNumber(data?.[index]?.amount?.value) ?? 0,
        },
      }));
    sheetFormik.setValues([...inputsToRender]);
    sheetFormik.isValid = false;
  };

  const updateTotals = () => {
    let totalTemp = 0;
    sheetFormik.values.forEach((product: any) => {
      totalTemp += toNumber(product.amount.value);
    });
    setTotalAmounts(totalTemp);
  };

  const addSheetMonth = () => {
    if (sheetFormik.values.length === 0) return;

    const sortedValues = [...sheetFormik.values].sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year
    );

    const lastAddedMonth = sortedValues[sortedValues.length - 1];

    let newMonth = lastAddedMonth.month + 1;
    let newYear = lastAddedMonth.year;

    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    if (
      newYear > currentYear ||
      (newYear === currentYear && newMonth > currentMonth)
    ) {
      setDisableAddSheetMonth(true);
      return;
    }

    const existingIndex = sheetFormik.values.findIndex(
      (entry) => entry.month === newMonth && entry.year === newYear
    );

    const updatedValues = [...sheetFormik.values];

    if (existingIndex !== -1) {
      updatedValues[existingIndex].amount.value = lastAddedMonth.amount.value;
    } else {
      updatedValues.push({
        id: Math.max(...updatedValues.map((e) => e.id), 0) + 1,
        monthId: Math.max(...updatedValues.map((e) => e.monthId), 0) + 1,
        month: newMonth,
        year: newYear,
        amount: {
          currency_code: CURRENCY_CODES.ars,
          value: 0,
        },
      });
    }
    sheetFormik.setValues(updatedValues);
  };

  const deleteSheetRow = (index: number) => {
    sheetFormik.setValues(sheetFormik.values.filter((_, i) => i !== index));
  };

  return (
    <Card spacing={{ left: '6' }}>
      <Card display='flex'>
        <Card width='31%'>
          <P type='2' weight='bold' variant='neutral'>
            Mes
          </P>
        </Card>
        <Card>
          <P type='2' weight='bold' variant='neutral'>
            {subtitle}
          </P>
        </Card>
      </Card>
      {sheetFormik.values.map((row: postBalanceSheetData, index: number) => (
        <Card display='flex' gap='16px' margin={{ bottom: '3' }} key={row.id}>
          <Card width='30%'>
            <DropDown
              id='risk-month-dropdown'
              items={months}
              size='md'
              title={
                months.find(
                  (item) => item.id === sheetFormik.values[index].monthId
                )
                  ? months.find(
                      (item) => item.id === sheetFormik.values[index].monthId
                    )?.label
                  : 'Seleccioná un mes'
              }
              onChange={(e) => {
                sheetFormik.setFieldTouched(`[${index}].monthId`, true, false);

                handleMonth(e);
              }}
              disabled={index > 0 || disableFields}
            />
            <Span variant='danger' type='2'>
              {sheetFormik.touched?.[index]?.monthId &&
                sheetFormik.errors[index]?.month}
            </Span>
          </Card>
          <Card width='70%'>
            <Card display='flex' gap='16px'>
              <div style={{ width: '100%', marginTop: inputMarginTop }}>
                <NumericFormat
                customInput={Input}
                inputStatus={
                  sheetFormik.errors[index]?.amount ? 'error' : 'normal'
                }
                id='risk-input-value'
                maxLength={40}
                placeholder={subtitle}
                value={row.amount.value ?? ''}
                thousandSeparator='.'
                decimalSeparator=','
                  style={{ width: '100%' }}
                  onValueChange={(values) => {
                    sheetFormik.setFieldTouched(
                      `[${index}].amount.value`,
                      true,
                      false
                    );
                    sheetFormik.setFieldValue(
                      `[${index}].amount.value`,
                      values.value === '' ? '' : values.floatValue
                    );
                  }}
                  decimalScale={2}
                  disabled={disableFields}
                />
              </div>
              {index === sheetFormik.values.length - 1 && index !== 0 && (
                <Button
                  size='md'
                  variant='secondary'
                  onClick={() => deleteSheetRow(index)}
                  disabled={disableFields}
                >
                  <Card variant='none'>
                    <Icon
                      variant='neutral'
                      name='delete_outline'
                      cursor='pointer'
                    />
                  </Card>
                </Button>
              )}
            </Card>
            <Span variant='danger' type='2'>
              {sheetFormik.touched?.[index]?.amount?.value &&
                sheetFormik.errors[index]?.amount}
            </Span>
          </Card>
        </Card>
      ))}
      <Button
        id='application-add-button'
        variant='tertiary'
        size='md'
        onClick={addSheetMonth}
        disabled={disableAddSheetMonth || disableFields}
      >
        + Agregar mes
      </Button>
      <Card
        border={true}
        display='flex'
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'3'}
        margin={{ top: '3' }}
        justifyContent='center'
        alignItems='center'
      >
        <Card display='flex' gap='24px'>
          <H type='3' withMargin={false} variant='neutral'>
            {totalText}:
          </H>
          <H type='3' withMargin={false} weight='bold' variant={clientTheme ? 'primary' : 'success'}>
            {CURRENCY_CODES.ars} {formatCurrency(totalAmounts ?? 0, true)}
          </H>
        </Card>
      </Card>
    </Card>
  );
};

export default PostBalanceSheet;
